import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  Switch,
} from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from "../../services/api"
import { API_ENDPOINTS } from "../../config/api"
import { useAuth } from '../../context/AuthContext';

export default function EditRole() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [features, setFeatures] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetchRole = async () => {
      try {
        const response = await api.get(API_ENDPOINTS.ROLE.GET_ONE(id));
        setFormData({
          name: response.data.role.name,
          description: response.data.role.description,
        });
      } catch (err) {
        console.error('Error fetching role:', err);
        setError('Failed to fetch role data. Please try again.');
      }
    };

    const fetchFeatures = async () => {
      try {
        const [featuresResponse, permissionsResponse] = await Promise.all([
          api.get(API_ENDPOINTS.FEATURE.GET_ALL),
          api.get(API_ENDPOINTS.ROLE_PERMISSION.GET_BY_ROLE_ID(id))
        ]);
      
        const fetchedFeatures = featuresResponse.data.features;
        setFeatures(fetchedFeatures);
      
        const permissionsMap = {};
        fetchedFeatures.forEach(feature => {
          permissionsMap[feature.id] = {
            read: false,
            create: false,
            update: false,
            delete: false,
          };
        });

        permissionsResponse.data.rolePermissions.forEach(permission => {
          permissionsMap[permission.feature_id.id] = {
            read: (permission.permission_mode & 1) !== 0,
            create: (permission.permission_mode & 2) !== 0,
            update: (permission.permission_mode & 4) !== 0,
            delete: (permission.permission_mode & 8) !== 0,
          };
        });
        setPermissions(permissionsMap);
        
        const allSelected = Object.values(permissionsMap).every(
          featurePermissions => Object.values(featurePermissions).every(Boolean)
        );
        setSelectAll(allSelected);
      } catch (err) {
        console.error('Error fetching features and permissions:', err);
        setError('Failed to fetch features and permissions. Please try again.');
      }
    };

    fetchRole();
    fetchFeatures();
    setLoading(false);
  }, [id, setFormData, setError, setFeatures, setPermissions, setSelectAll]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api.put(API_ENDPOINTS.ROLE.UPDATE(id), {
        ...formData,
        client_id: user.clientId,
      });

      for (const [featureId, featurePermissions] of Object.entries(permissions)) {
        const permissionMode = calculatePermissionMode(featurePermissions);
        await api.post(API_ENDPOINTS.ROLE_PERMISSION.CREATE, {
          role_id: id,
          feature_id: featureId,
          permission_mode: permissionMode,
        });
      }

      navigate('/roles');
    } catch (err) {
      console.error('Error updating role:', err);
      setError('Failed to update role. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculatePermissionMode = (featurePermissions) => {
    let mode = 0;
    if (featurePermissions.read) mode |= 1;
    if (featurePermissions.create) mode |= 2;
    if (featurePermissions.update) mode |= 4;
    if (featurePermissions.delete) mode |= 8;
    return mode;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handlePermissionChange = (featureId, permission) => {
    setPermissions(prevPermissions => ({
      ...prevPermissions,
      [featureId]: {
        ...prevPermissions[featureId],
        [permission]: !prevPermissions[featureId]?.[permission],
      },
    }));
    
    const allSelected = Object.values(permissions).every(
      featurePermissions => Object.values(featurePermissions).every(Boolean)
    );
    setSelectAll(allSelected);
  };

  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    setSelectAll(checked);
    setPermissions(prevPermissions => {
      const newPermissions = {};
      features.forEach(feature => {
        newPermissions[feature.id] = {
          read: checked,
          create: checked,
          update: checked,
          delete: checked,
        };
      });
      return newPermissions;
    });
  };

  const handleClearAll = () => {
    setSelectAll(false);
    setPermissions(prevPermissions => {
      const newPermissions = {};
      for (const featureId in prevPermissions) {
        newPermissions[featureId] = {
          read: false,
          create: false,
          update: false,
          delete: false,
        };
      }
      return newPermissions;
    });
  };

  return (
    <Box sx={{ width: '100%', pl: 1, pr: 2, pt: 0, backgroundColor: 'background.paper', borderRadius: 8 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={() => navigate('/roles')}
          sx={{ 
            mr: 2, 
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
            }
          }}
        >
          <ChevronLeft />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Edit Role
        </Typography>
      </Box>

      <Paper 
        sx={{ 
          p: 2, 
          borderRadius: 6,
          boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
          backgroundColor: 'background.paper'
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          <Box sx={{ mb: 3 }}>
            <TextField
              required
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 8,
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#C41747',
                  },
                },
              }}
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 8,
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#C41747',
                  },
                },
              }}
            />
          </Box>

          <Typography variant="h6" sx={{ mb: 2 }}>Feature Permissions</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={selectAll}
                  onChange={handleSelectAll}
                  color="primary"
                />
              }
              label="Select All Permissions"
            />
            <Button
              variant="outlined"
              onClick={handleClearAll}
              sx={{
                color: '#6C757D',
                borderColor: '#E0E0E0',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#6C757D',
                  bgcolor: 'transparent',
                },
              }}
            >
              Clear All
            </Button>
          </Box>
          <Grid container spacing={2}>
            {features.map((feature) => {
              if (feature.name === 'client') {
                return null;
              }

              return (
                <Grid item xs={12} sm={6} md={4} key={feature.id}>
                  <Card sx={{ backgroundColor: 'background.default' }}>
                    <CardContent>
                    <Typography variant="subtitle1">{feature.description}</Typography>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={permissions[feature.id]?.read || false}
                          onChange={() => handlePermissionChange(feature.id, 'read')}
                        />
                      }
                      label="View"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={permissions[feature.id]?.create || false}
                          onChange={() => handlePermissionChange(feature.id, 'create')}
                        />
                      }
                      label="Create"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={permissions[feature.id]?.update || false}
                          onChange={() => handlePermissionChange(feature.id, 'update')}
                        />
                      }
                      label="Update"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={permissions[feature.id]?.delete || false}
                          onChange={() => handlePermissionChange(feature.id, 'delete')}
                        />
                      }
                      label="Delete"
                    />
                  </CardContent>
                </Card>
              </Grid>
              );
            })}
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: '#E31B54',
                color: '#FFFFFF',
                textTransform: 'none',
                borderRadius: 18,
                px: 4,
                '&:hover': { bgcolor: '#C41747' },
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Update'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/roles')}
              sx={{
                color: "#F9826E",
                borderColor: "#F9826E",
                textTransform: "none",
                borderRadius: 18,
                "&:hover": {
                  color: "black",
                  borderColor: "#F7583D",
                  bgcolor: "#F9826E",
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
